import React, { useMemo } from 'react';
import EdgedocsComposer from '../components/composers/EdgedocsComposer';

export const TemplatePreviewPage: React.FC = () => {
  const data = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('webelieve.templatePreview');
      return raw ? JSON.parse(raw) as { name: string; lexicalJson: unknown; styleTokens?: any } : null;
    } catch {
      return null;
    }
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200">
        <div className="text-center">
          <div className="text-red-600 text-4xl mb-2">⚠️</div>
          <div className="text-sm">No template preview data found.</div>
        </div>
      </div>
    );
  }

  function buildSampleMapping(): Record<string, string> {
    return {
      ATTORNEY_BLOCK: 'John Q. Lawyer (SBN 123456)\nLaw Firm LLP\n123 Main St, Suite 500\nLos Angeles, CA 90012',
      COURT_NAME: 'Superior Court of California',
      COUNTY_NAME: 'County of Los Angeles',
      PLAINTIFF: 'PLAINTIFF NAME',
      DEFENDANT: 'DEFENDANT NAME',
      CASE_NUMBER: '22STCV00001',
      DEPT: '44',
      JUDGE: 'Hon. Jane Doe',
      TITLE_OF_DOCUMENT: 'NOTICE OF MOTION AND MOTION TO COMPEL',
      HEARING_DATE: 'October 1, 2025',
      HEARING_TIME: '8:30 a.m.',
      HEARING_LOCATION: 'Dept. 44, Stanley Mosk Courthouse',
      INTRO: 'PLEASE TAKE NOTICE that on the above date and time, Plaintiff will move this Court for an order…',
      FACTS: 'This action arises from… Relevant background is as follows…',
      ARGUMENT: 'I. Legal Standard.\nII. Application to Facts.\nIII. Relief Requested.',
      PRAYER: 'For the foregoing reasons, Plaintiff respectfully requests that the Court grant the motion…',
      SIGNATURE: 'Dated: ____________\n\nLAW FIRM LLP\n\nBy: ______________________________\nJohn Q. Lawyer\nAttorneys for Plaintiff',
    };
  }

  function replaceTokens(text: string, mapping: Record<string, string>): string {
    return text.replace(/\{\{([A-Z_]+)\}\}/g, (_m, key: string) => mapping[key] ?? _m);
  }

  function transformLexicalJson(rootObj: any, mapping: Record<string, string>): any {
    function walk(node: any): any {
      if (Array.isArray(node)) {
        return node.map(walk);
      }
      if (node && typeof node === 'object') {
        const copy: any = { ...node };
        if (typeof copy.text === 'string') {
          copy.text = replaceTokens(copy.text, mapping);
        }
        if (Array.isArray(copy.children)) {
          copy.children = copy.children.map(walk);
        }
        return copy;
      }
      return node;
    }
    return walk(rootObj);
  }

  const mapping = buildSampleMapping();
  const transformed = transformLexicalJson(data.lexicalJson || {}, mapping);
  const initialContent = JSON.stringify(transformed);
  const pp = (data.styleTokens || {}) as { margins?: any; pleadingPaper?: any };
  const ppOptions = {
    marginTopIn: pp.margins?.top ?? 1,
    marginRightIn: pp.margins?.right ?? 1,
    marginBottomIn: pp.margins?.bottom ?? 1,
    marginLeftIn: pp.margins?.left ?? 1,
    gutterIn: pp.pleadingPaper?.gutter ?? 0.5,
    lineCount: pp.pleadingPaper?.lineNumberEnd ?? 28,
  };

  return (
    <div className="h-screen bg-white dark:bg-gray-900">
      <EdgedocsComposer
        documentId={`template-preview-${Date.now()}`}
        versionId=""
        initialContent={initialContent}
        onClose={() => window.close()}
        readOnly={false}
        fullScreen={true}
        useStore={false}
        documentTitle={`Preview: ${data.name}`}
        documentType="pleading"
        autoApplyTemplate={false}
        enablePlugins={true}
        pleadingPaperOptions={ppOptions}
      />
    </div>
  );
};

export default TemplatePreviewPage;


